import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { JobStatus, FinalDecision, ReviewStatus } from '@/types';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface JobListItem {
  id: string;
  file_name: string;
  status: JobStatus;
  final_decision: FinalDecision | null;
  review_status: ReviewStatus | null;
  processing_time_ms: number | null;
  submitted_by_name: string | null;
  reviewed_by_name: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface JobListFilters {
  status?: JobStatus | '';
  decision?: FinalDecision | '';
  review_status?: ReviewStatus | '';
  date_from?: string;
  date_to?: string;
  page?: number;
  limit?: number;
}

interface JobListResponse {
  items: JobListItem[];
  total: number;
  page: number;
  limit: number;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Fetches paginated job list for the Overview tab table.
 * Supports filtering by status, decision, review_status, and date range.
 */
export function useJobList(filters: JobListFilters = {}) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.decision) params.set('decision', filters.decision);
  if (filters.review_status) params.set('review_status', filters.review_status);
  if (filters.date_from) params.set('date_from', filters.date_from);
  if (filters.date_to) params.set('date_to', filters.date_to);
  params.set('page', String(filters.page ?? 1));
  params.set('limit', String(filters.limit ?? 10));

  const queryString = params.toString();

  return useQuery<JobListResponse>({
    queryKey: ['jobs', 'list', filters],
    queryFn: () => api.get<JobListResponse>(`/api/v1/jobs/?${queryString}`),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}
